<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useFontSize } from 'dashboard/composables/useFontSize';
import { useBranding } from 'shared/composables/useBranding';
import { clearCookiesOnLogout } from 'dashboard/store/utils/api.js';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import { parseBoolean } from '@chatwoot/utils';
import UserProfilePicture from './UserProfilePicture.vue';
import UserBasicDetails from './UserBasicDetails.vue';
import MessageSignature from './MessageSignature.vue';
import FontSize from './FontSize.vue';
import UserLanguageSelect from './UserLanguageSelect.vue';
import ChangePassword from './ChangePassword.vue';
import NotificationPreferences from './NotificationPreferences.vue';
import AudioNotifications from './AudioNotifications.vue';
import FormSection from 'dashboard/components/FormSection.vue';
import AccessToken from './AccessToken.vue';
import MfaSettingsCard from './MfaSettingsCard.vue';
import Policy from 'dashboard/components/policy.vue';
import {
  ROLES,
  CONVERSATION_PERMISSIONS,
} from 'dashboard/constants/permissions.js';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { currentFontSize, updateFontSize } = useFontSize();
const { replaceInstallationName } = useBranding();
const currentUser = computed(() => store.getters.getCurrentUser);
const currentUserId = computed(() => store.getters.getCurrentUserID);
const globalConfig = computed(() => store.getters['globalConfig/get']);
const isMfaEnabled = computed(() =>
  parseBoolean(window.chatwootConfig?.isMfaEnabled)
);
const avatarFile = ref('');
const profile = reactive({
  name: '',
  email: '',
  displayName: '',
  avatarUrl: '',
  messageSignature: '',
});
const notificationPermissions = [...ROLES, ...CONVERSATION_PERMISSIONS];
const tabs = computed(() => [
  { value: 'data', label: t('SETTINGS_NAV.DATA') },
  { value: 'preferences', label: t('SETTINGS_NAV.PREFERENCES') },
  { value: 'integrations', label: t('SETTINGS_NAV.INTEGRATIONS') },
]);
const activeTab = computed(() =>
  tabs.value.some(tab => tab.value === route.query.tab)
    ? route.query.tab
    : 'data'
);
const selectTab = tab => router.replace({ query: { ...route.query, tab } });
watch(
  currentUserId,
  id => {
    if (!id) return;
    Object.assign(profile, {
      name: currentUser.value.name,
      email: currentUser.value.email,
      displayName: currentUser.value.display_name,
      avatarUrl: currentUser.value.avatar_url,
      messageSignature: currentUser.value.message_signature || '',
    });
  },
  { immediate: true }
);

const dispatchUpdate = async (payload, successMessage, errorMessage) => {
  try {
    await store.dispatch('updateProfile', payload);
    useAlert(successMessage);
    return true;
  } catch (error) {
    useAlert(parseAPIErrorResponse(error) || errorMessage);
    return false;
  }
};
const updateProfile = async ({ name, email, displayName }) => {
  const hasEmailChanged = currentUser.value.email !== email;
  profile.name = name || profile.name;
  profile.email = email || profile.email;
  profile.displayName = displayName ?? profile.displayName;
  const success = await dispatchUpdate(
    {
      name: profile.name,
      email: profile.email,
      displayName: profile.displayName,
      avatar: avatarFile.value,
    },
    hasEmailChanged
      ? t('PROFILE_SETTINGS.AFTER_EMAIL_CHANGED')
      : t('PROFILE_SETTINGS.UPDATE_SUCCESS'),
    t('RESET_PASSWORD.API.ERROR_MESSAGE')
  );
  if (hasEmailChanged && success) clearCookiesOnLogout();
};
const updateSignature = signature =>
  dispatchUpdate(
    { message_signature: signature },
    t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.API_SUCCESS'),
    t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.API_ERROR')
  );
const updateProfilePicture = ({ file, url }) => {
  avatarFile.value = file;
  profile.avatarUrl = url;
};
const deleteProfilePicture = async () => {
  try {
    await store.dispatch('deleteAvatar');
    profile.avatarUrl = '';
    avatarFile.value = '';
    useAlert(t('PROFILE_SETTINGS.AVATAR_DELETE_SUCCESS'));
  } catch {
    useAlert(t('PROFILE_SETTINGS.AVATAR_DELETE_FAILED'));
  }
};
const onCopyToken = async value => {
  await copyTextToClipboard(value);
  useAlert(t('COMPONENTS.CODE.COPY_SUCCESSFUL'));
};
const resetAccessToken = async () => {
  const success = await store.dispatch('resetAccessToken');
  useAlert(
    success
      ? t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.RESET_SUCCESS')
      : t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.RESET_ERROR')
  );
};
const cardClass = 'rounded-2xl border border-n-weak bg-n-solid-1 p-5 sm:p-6';
</script>

<template>
  <div class="flex min-h-0 min-w-0 flex-1 flex-col font-inter">
    <header
      class="shrink-0 border-b border-n-weak bg-n-solid-1 px-5 pt-4 sm:px-6"
    >
      <h1 class="text-base font-semibold text-n-slate-12">
        {{ t('SETTINGS_NAV.PROFILE') }}
      </h1>
      <p class="mb-4 mt-1 text-xs text-n-slate-11">
        {{ t('SETTINGS_NAV.PROFILE_HELP') }}
      </p>
      <div
        role="tablist"
        :aria-label="t('SETTINGS_NAV.PROFILE')"
        class="flex gap-6"
      >
        <button
          v-for="tab in tabs"
          :id="`profile-tab-${tab.value}`"
          :key="tab.value"
          type="button"
          role="tab"
          :aria-selected="activeTab === tab.value"
          :aria-controls="`profile-panel-${tab.value}`"
          class="border-0 border-b-2 border-solid bg-transparent px-0 py-3 text-sm transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
          :class="
            activeTab === tab.value
              ? 'border-n-brand font-medium text-n-slate-12'
              : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
          "
          @click="selectTab(tab.value)"
        >
          {{ tab.label }}
        </button>
      </div>
    </header>
    <div class="min-h-0 flex-1 overflow-y-auto bg-n-background p-4 sm:p-6">
      <div class="mx-auto max-w-5xl">
        <section
          v-show="activeTab === 'data'"
          id="profile-panel-data"
          role="tabpanel"
          aria-labelledby="profile-tab-data"
          class="grid gap-5"
        >
          <FormSection
            :class="cardClass"
            :title="t('SETTINGS_NAV.PHOTO')"
            :description="t('SETTINGS_NAV.PHOTO_HELP')"
          >
            <UserProfilePicture
              :src="profile.avatarUrl"
              :name="profile.name"
              :show-label="false"
              @change="updateProfilePicture"
              @delete="deleteProfilePicture"
            />
          </FormSection>
          <FormSection
            :class="cardClass"
            :title="t('SETTINGS_NAV.DATA')"
            :description="t('SETTINGS_NAV.DATA_HELP')"
          >
            <UserBasicDetails
              :name="profile.name"
              :display-name="profile.displayName"
              :email="profile.email"
              :email-enabled="!globalConfig.disableUserProfileUpdate"
              @update-user="updateProfile"
            />
          </FormSection>
          <FormSection
            v-if="!globalConfig.disableUserProfileUpdate"
            :class="cardClass"
            :title="t('PROFILE_SETTINGS.FORM.PASSWORD_SECTION.TITLE')"
          >
            <ChangePassword />
          </FormSection>
          <FormSection
            v-if="isMfaEnabled"
            :class="cardClass"
            :title="t('PROFILE_SETTINGS.FORM.SECURITY_SECTION.TITLE')"
            :description="t('PROFILE_SETTINGS.FORM.SECURITY_SECTION.NOTE')"
          >
            <MfaSettingsCard />
          </FormSection>
        </section>
        <section
          v-show="activeTab === 'preferences'"
          id="profile-panel-preferences"
          role="tabpanel"
          aria-labelledby="profile-tab-preferences"
          class="grid gap-5"
        >
          <FormSection
            :class="cardClass"
            :title="t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.TITLE')"
            :description="
              replaceInstallationName(
                t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.NOTE')
              )
            "
          >
            <FontSize
              :value="currentFontSize"
              :label="
                t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.FONT_SIZE.TITLE')
              "
              :description="
                t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.FONT_SIZE.NOTE')
              "
              @change="updateFontSize"
            />
            <UserLanguageSelect
              :label="
                t(
                  'PROFILE_SETTINGS.FORM.INTERFACE_SECTION.YESZAP_LANGUAGE.TITLE'
                )
              "
              :description="
                t(
                  'PROFILE_SETTINGS.FORM.INTERFACE_SECTION.YESZAP_LANGUAGE.NOTE'
                )
              "
            />
          </FormSection>
          <FormSection
            :class="cardClass"
            :title="t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.TITLE')"
            :description="
              t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.NOTE')
            "
          >
            <MessageSignature
              :message-signature="profile.messageSignature"
              @update-signature="updateSignature"
            />
          </FormSection>
          <Policy :permissions="notificationPermissions">
            <FormSection
              :class="cardClass"
              :title="
                t('PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.TITLE')
              "
              :description="
                t('PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.NOTE')
              "
            >
              <AudioNotifications />
            </FormSection>
          </Policy>
          <Policy :permissions="notificationPermissions">
            <FormSection
              :class="cardClass"
              :title="t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.TITLE')"
            >
              <NotificationPreferences />
            </FormSection>
          </Policy>
        </section>
        <section
          v-show="activeTab === 'integrations'"
          id="profile-panel-integrations"
          role="tabpanel"
          aria-labelledby="profile-tab-integrations"
          class="grid gap-5"
        >
          <FormSection
            :class="cardClass"
            :title="t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.TITLE')"
            :description="
              replaceInstallationName(
                t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.NOTE')
              )
            "
          >
            <AccessToken
              :value="currentUser.access_token"
              @on-copy="onCopyToken"
              @on-reset="resetAccessToken"
            />
          </FormSection>
        </section>
      </div>
    </div>
  </div>
</template>
